import { useEffect, useRef, useState } from 'react';
import { ExportStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { http } from '@frontend/fetch';
import { InProgressType, AddEventInProgressType } from '../types';

const DEFAULT_ONE_MINUTE_MS = 60000;
export const LOCAL_STORAGE_KEY = 'downloadEventsInProgress';

function addEventToLocalStorage(event: AddEventInProgressType) {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  let events: Record<string, AddEventInProgressType> = {};
  if (stored && stored !== 'undefined') {
    events = JSON.parse(stored);
  }
  events[event.id] = event;
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(events));
}

function removeEventFromLocalStorage(id: string) {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored && stored !== 'undefined') {
    const events: Record<string, AddEventInProgressType> = JSON.parse(stored);
    delete events[id];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(events));
  }
}

const checkDownloadStatus = async (url: string) => {
  const response = await http.get(url);
  return response;
};

const checkDownloadStatusType = (response: any): ExportStatus => {
  if (response && (response as any).status === ExportStatus.EXPORT_STATUS_COMPLETED) {
    return ExportStatus.EXPORT_STATUS_COMPLETED;
  }
  if (response && (response as any).status === ExportStatus.EXPORT_STATUS_FAILED) {
    return ExportStatus.EXPORT_STATUS_FAILED;
  }
  return ExportStatus.EXPORT_STATUS_PENDING;
};

const getLocalStorage = () => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored && stored !== 'undefined') {
    return JSON.parse(stored) as InProgressType;
  }
  return {};
};

/**
 * useEventDownloadWaitState Hook
 *
 * This hook manages the state of CSV download events that uses websocket events to notify when a download is ready.
 * It allows adding events to track and polling for their status if the session is disconnected
 * Handle any websocket events on your own but you would need to still clear the event here.
 *
 * How to use this hook:
 *
 * Add event to inProgressState → adds to local storage and updates state.
 * If websocket event received, you need to handle the cleanup by calling handleClearEvent.
 * If you don't, the status bar will still persist.
 * If session is refreshed, the state's default is to get the local storage and starts polling for each event in progress
 * This happens by the useEffect that is called once onMount
 */

export const useEventDownloadWaitState = () => {
  const [inProgressState, setInProgressState] = useState<InProgressType>(getLocalStorage());
  const timeoutRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const cleanup = (id: string) => {
    setInProgressState((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id]);
      delete timeoutRefs.current[id];
    }
    removeEventFromLocalStorage(id);
  };

  const addEventInProgress = (options: Omit<AddEventInProgressType, 'isWaiting'>) => {
    const event = {
      ...options,
      isWaiting: true,
    };

    addEventToLocalStorage(event);
    setInProgressState((prev) => ({
      ...prev,
      [options.id]: event,
    }));
  };

  const pollingDownloadEvent = (options: AddEventInProgressType) => {
    const { id, url } = options;
    const poll = async () => {
      try {
        const response = await checkDownloadStatus(url);
        const downloadStatus = checkDownloadStatusType(response);
        if (downloadStatus === ExportStatus.EXPORT_STATUS_COMPLETED) {
          cleanup(id);
        } else if (downloadStatus === ExportStatus.EXPORT_STATUS_PENDING) {
          timeoutRefs.current[id] = setTimeout(poll, DEFAULT_ONE_MINUTE_MS);
        } else {
          cleanup(id);
        }
      } catch (error) {
        cleanup(id);
      }
    };
    poll();
  };

  useEffect(() => {
    if (inProgressState && Object.keys(inProgressState).length > 0) {
      Object.values(inProgressState).forEach((event) => {
        pollingDownloadEvent(event);
      });
    }
  }, []);

  return {
    inProgressState,
    addEventInProgress,
    handleClearEvent: cleanup,
  };
};
